import React from 'react';
import classNames from 'classnames';

// Section components
import SectionArticle from './SectionArticle';
import SectionCarousel from './SectionCarousel';
import SectionColumns from './SectionColumns';
import SectionFeatures from './SectionFeatures';
import SectionFooter from './SectionFooter';
import SectionHero from './SectionHero';
import SectionListings from './SectionListings';
import FeatureMemberClosests from '../CustomSections/FeatureMemberClosests';
import FeaturedRentals from '../CustomSections/FeaturedRentals';
import HowItWorksSection from '../CustomSections/HowItWorksSection';
import OccasionDressingCarousel from '../CustomSections/OccasionDressingCarousel';
import NewArrivalsSection from '../CustomSections/NewArrivalsSection';
import RewardYouSection from '../CustomSections/RewardYouSection';
import FaqSection from '../CustomSections/FaqSection';
import JournalCardGrid from '../CustomSections/JournalCardGrid';
import RelatedJournalCards from '../CustomSections/RelatedJournalCards';

// inner page css files
import '../../../styles/innerPages/ourStory.css';
import '../../../styles/innerPages/howItWork.css';
import '../../../styles/innerPages/exchangeNship.css';
import '../../../styles/innerPages/becomeAnAmbassador.css';
import '../../../styles/innerPages/sustainability.css';
import '../../../styles/innerPages/privacyPolicyProtection.css';
import '../../../styles/innerPages/feeSeclude.css';
import '../../../styles/innerPages/concierge.css';
import '../../../styles/innerPages/helpCenter.css';
import '../../../styles/innerPages/contact.css';
import '../../../styles/innerPages/shippingCourierAndClaimsPolicy.css';
import '../../../styles/innerPages/termsOfService.css';
import '../../../styles/innerPages/journal.css';
import '../../../styles/innerPages/journalArticle.css';


// Styles
// Note: these contain
// - shared classes that are passed as defaultClasses
// - dark theme overrides
// TODO: alternatively, we could consider more in-place way of theming components
import css from './SectionBuilder.module.css';

// These are shared classes.
// Use these to have consistent styles between different section components
// E.g. share the same title styles
const DEFAULT_CLASSES = {
  sectionDetails: css.sectionDetails,
  title: css.title,
  description: css.description,
  ctaButton: css.ctaButton,
  blockContainer: css.blockContainer,
  defaultLink: css.defaultLink,
};

/////////////////////////////////////////////
// Mapping of section types and components //
/////////////////////////////////////////////

const defaultSectionComponents = {
  article: { component: SectionArticle },
  carousel: { component: SectionCarousel },
  columns: { component: SectionColumns },
  features: { component: SectionFeatures },
  footer: { component: SectionFooter },
  hero: { component: SectionHero },
  listings: { component: SectionListings },
  'featured-rentals': { component: SectionListings },
  'feature-member-closets': { component: FeatureMemberClosests },
  'featured-rentals': { component: FeaturedRentals },
  'how-it-works-section': { component: HowItWorksSection },
  'occasion-dressing-section': { component: OccasionDressingCarousel },
  'new-arrivals-section': { component: NewArrivalsSection },
  'new-arrivals': { component: NewArrivalsSection },
  'reward-you-section': { component: RewardYouSection },
  'faq-section': { component: FaqSection },
  faq: { component: FaqSection },
  'journal-card-grid-container': { component: JournalCardGrid },
  'detail-journal-related-cards': { component: RelatedJournalCards },
};

// Console sectionId → custom section (works even if sectionType is columns/carousel/etc.)
const customSectionsById = {
  'featured-rentals': SectionListings,
  'feature-member-closets': FeatureMemberClosests,
  'featured-rentals': FeaturedRentals,
  'how-it-works-section': HowItWorksSection,
  'occasion-dressing-section': OccasionDressingCarousel,
  'new-arrivals-section': NewArrivalsSection,
  'new-arrivals': NewArrivalsSection,
  'reward-you-section': RewardYouSection,
  'faq-section': FaqSection,
  'journal-card-grid-container': JournalCardGrid,
  'detail-journal-related-cards': RelatedJournalCards,
};

//////////////////////
// Section builder //
//////////////////////

/**
 * @typedef {Object} FieldOption
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionOption
 * @property {ReactNode} component
 */

/**
 * @typedef {Object} SectionConfig
 * @property {string} sectionId
 * @property {string} sectionName
 * @property {('article' | 'carousel' | 'columns' | 'features' | 'hero' | 'listings')} sectionType
 */

/**
 * Build section elements from given section config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<SectionConfig>} props.sections
 * @param {Object} props.options
 * @param {Object<string,FieldOption>} props.options.fieldComponents
 * @param {Object<string,BlockOption>} props.options.blockComponents
 * @param {Object<string,SectionOption>} props.options.sectionComponents
 * @param {boolean} props.options.isInsideContainer
 * @returns {JSX.Element} element containing array of sections according from given config array.
 */
const SectionBuilder = props => {
  const { sections = [], options } = props;
  const { sectionComponents = {}, isInsideContainer, ...otherOption } = options || {};

  // If there's no sections, we can't render the correct section component
  if (!sections || sections.length === 0) {
    return null;
  }

  // Selection of Section components
  const components = { ...defaultSectionComponents, ...sectionComponents };
  const getComponent = (sectionType, sectionId) => {
    if (sectionId && customSectionsById[sectionId]) {
      return customSectionsById[sectionId];
    }
    const config = components[sectionType];
    return config?.component;
  };

  // Generate unique ids for sections if operator has managed to create duplicates
  // E.g. "foobar", "foobar1", and "foobar2"
  const sectionIds = [];
  const getUniqueSectionId = (sectionId, index) => {
    const candidate = sectionId || `section-${index + 1}`;
    if (sectionIds.includes(candidate)) {
      let sequentialCandidate = `${candidate}1`;
      for (let i = 2; sectionIds.includes(sequentialCandidate); i++) {
        sequentialCandidate = `${candidate}${i}`;
      }
      return getUniqueSectionId(sequentialCandidate, index);
    } else {
      sectionIds.push(candidate);
      return candidate;
    }
  };

  // Resolve all section ids
  const sectionsWithResolvedIds = sections.map((section, index) => ({
    ...section,
    sectionId: getUniqueSectionId(section.sectionId, index),
  }));

  return (
    <>
      {sectionsWithResolvedIds.map((section, index) => {
        const Section = getComponent(section.sectionType, section.sectionId);
        // If the default "dark" theme should be applied (when text color is white).
        // By default, this information is stored to customAppearance field
        const isDarkTheme =
          section?.appearance?.fieldType === 'customAppearance' &&
          section?.appearance?.textColor === 'white';
        const classes = classNames({ [css.darkTheme]: isDarkTheme });
        const sectionId = section.sectionId;

        if (Section) {
          return (
            <Section
              key={`${sectionId}_i${index}`}
              className={classes}
              defaultClasses={DEFAULT_CLASSES}
              isInsideContainer={isInsideContainer}
              options={{ ...otherOption, defaultClasses: DEFAULT_CLASSES }}
              {...section}
              sectionId={sectionId}
              allSections={sectionsWithResolvedIds}
            />
          );
        } else {
          // If the section type is unknown, the app can't know what to render
          console.warn(
            `Unknown section type (${section.sectionType}) detected using sectionName (${section.sectionName}).`
          );
          return null;
        }
      })}
    </>
  );
};

export default SectionBuilder;
